import type { Page } from '@hello-aria/makalu-api-client'
import { isNil } from 'lodash'
import type { InfiniteData, UseInfiniteQueryResult, UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
    Invoice,
    UpdateInvoice,
    UpdateInvoiceAttachments,
    CreateInvoice,
    UnlinkReceivable,
    LinkReceivable,
    InvoiceGateway
} from '@/shared/models/invoiceModels'
import {
    createInvoice,
    getInvoice,
    listInvoices,
    linkReceivableToInvoice,
    unlinkReceivableFromInvoice,
    updateInvoice
} from '@/shared/services/invoicesServices'
import { getPageSize } from '@/shared/utils/queryUtils'
import { useMe } from './useMe'
import { receivablesQueryKeys } from './useReceivables'
import type { QueryStringInput } from '@hello-aria/query-string/dist/stringify'
import { isValidUUID } from '@/shared/utils/validatorUtils'
import type { State } from '@/shared/services/uploadServices'
import { updateInvoiceAttachments } from '@/shared/services/uploadServices'

export const invoicesQueryKeys = {
    root: ['invoices'],
    infiniteInvoices: (companyId: string, query?: QueryStringInput): (string | undefined | QueryStringInput)[] => [
        ...invoicesQueryKeys.root,
        companyId,
        query
    ],
    invoiceById: (invoiceId: string | null | undefined, companyId: string): (string | null | undefined)[] => [
        ...invoicesQueryKeys.root,
        companyId,
        invoiceId
    ]
}

type UseInfiniteInvoices = {
    query?: QueryStringInput
    isEnabled?: boolean
}

export const useInfiniteInvoices = (
    options?: UseInfiniteInvoices
): UseInfiniteQueryResult<InfiniteData<Page<Invoice>>> => {
    const { isEnabled, query } = options || {}
    const { companyId } = useMe()

    return useInfiniteQuery({
        queryKey: invoicesQueryKeys.infiniteInvoices(companyId, query),

        queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
            return listInvoices({
                hasAttachments: false,
                companyId,
                query: {
                    pagination: {
                        after: pageParam,
                        first: getPageSize(query)
                    },
                    ...query
                }
            })
        },

        enabled: isEnabled,

        getNextPageParam: (lastPage) => {
            if (lastPage?.pagination?.hasNextPage) return lastPage?.pagination?.endCursor
            return undefined
        },

        initialPageParam: undefined
    })
}

export const useInvoice = (
    invoiceId: string | null | undefined,
    hasAttachments = true
): UseQueryResult<Invoice | undefined> => {
    const { companyId } = useMe()
    return useQuery({
        queryKey: invoicesQueryKeys.invoiceById(invoiceId, companyId),
        queryFn: async () => {
            const response = await getInvoice({ invoiceId, companyId, hasAttachments })

            return response
        },
        enabled: !isNil(invoiceId) && isValidUUID(invoiceId)
    })
}

export const useCreateInvoice = (): UseMutationResult<Invoice | undefined, unknown, CreateInvoice> => {
    const { companyId } = useMe()

    return useMutation({
        mutationFn: (payload) => {
            return createInvoice(companyId, payload)
        }
    })
}

export const useUpdateInvoice = (): UseMutationResult<Invoice | undefined, unknown, UpdateInvoice> => {
    const { companyId } = useMe()

    return useMutation({
        mutationFn: (payload) => {
            return updateInvoice(companyId, payload)
        }
    })
}

export const useUpdateInvoiceAttachments = (
    state: State
): UseMutationResult<InvoiceGateway, unknown, UpdateInvoiceAttachments> => {
    const { companyId } = useMe()

    return useMutation({
        mutationFn: (payload) => updateInvoiceAttachments(companyId, payload, state)
    })
}

export const useInvalidateInvoices = () => {
    const queryClient = useQueryClient()
    const { companyId } = useMe()

    const invalidateInvoices = async () => {
        await queryClient.invalidateQueries({
            queryKey: invoicesQueryKeys.infiniteInvoices(companyId)
        })
    }

    const invalidateInvoice = async (invoiceId: string) => {
        await queryClient.invalidateQueries({
            queryKey: invoicesQueryKeys.invoiceById(invoiceId, companyId),
            exact: true
        })
    }

    return { invalidateInvoices, invalidateInvoice }
}

export const useUnlinkReceivableFromInvoice = () => {
    const queryClient = useQueryClient()
    const { companyId } = useMe()

    return useMutation({
        mutationFn: (payload: UnlinkReceivable) => {
            return unlinkReceivableFromInvoice(companyId, payload)
        },
        // Settled, not success: in a batch, the receivables unlinked before a failure have changed.
        onSettled: () => queryClient.invalidateQueries({ queryKey: receivablesQueryKeys.root })
    })
}

export const useLinkReceivablesToInvoice = () => {
    const { companyId } = useMe()

    return useMutation({
        mutationFn: (payload: LinkReceivable) => {
            return linkReceivableToInvoice(companyId, payload)
        }
    })
}
